export type PdfInlineRun = {
    text: string;
    italic?: boolean;
    bold?: boolean;
    sub?: boolean;
    sup?: boolean;
};

export type PdfBlock = {
    kind: "h1" | "h2" | "p" | "byline";
    runs: PdfInlineRun[];
};

type PdfJsTextItem = {
    str: string;
    transform: number[];
    width: number;
    height: number;
    fontName: string;
    hasEOL?: boolean;
};

type PdfJsTextStyle = {
    fontFamily?: string;
};

export type PdfJsTextContent = {
    items: Array<PdfJsTextItem | { type?: string }>;
    styles?: Record<string, PdfJsTextStyle>;
};

type Token = {
    str: string;
    x: number;
    y: number;
    width: number;
    fontSize: number;
    italic: boolean;
    bold: boolean;
    hasEOL: boolean;
};

function isTextItem(item: PdfJsTextContent["items"][number]): item is PdfJsTextItem {
    return "str" in item && typeof (item as PdfJsTextItem).str === "string" && Array.isArray((item as PdfJsTextItem).transform);
}

function fontSizeOf(transform: number[]): number {
    return Math.hypot(transform[2], transform[3]) || Math.abs(transform[3]) || Math.abs(transform[0]) || 12;
}

function isHorizontal(transform: number[]): boolean {
    return Math.abs(transform[0]) >= Math.abs(transform[1]);
}

function looksItalic(fontName: string, family?: string): boolean {
    return /italic|oblique|cmmi/i.test(`${fontName} ${family ?? ""}`);
}

function looksBold(fontName: string, family?: string): boolean {
    return /bold|black|heavy|semibold/i.test(`${fontName} ${family ?? ""}`);
}

function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

function mergeRuns(runs: PdfInlineRun[]): PdfInlineRun[] {
    const out: PdfInlineRun[] = [];
    for (const run of runs) {
        if (!run.text) continue;
        const prev = out[out.length - 1];
        if (
            prev &&
            !!prev.italic === !!run.italic &&
            !!prev.bold === !!run.bold &&
            !!prev.sub === !!run.sub &&
            !!prev.sup === !!run.sup
        ) {
            prev.text += run.text;
        } else {
            out.push({ ...run });
        }
    }
    return out;
}

function lineRuns(line: Token[]): PdfInlineRun[] {
    if (line.length === 0) return [];
    const maxFs = Math.max(...line.map((t) => t.fontSize));
    const body = line.filter((t) => t.fontSize >= maxFs * 0.85);
    const baseline = median(body.map((t) => t.y));
    const subThresh = maxFs * 0.12;

    const runs: PdfInlineRun[] = [];
    for (let i = 0; i < line.length; i++) {
        const t = line[i];
        const next = line[i + 1];
        const sub = t.y < baseline - subThresh && t.fontSize < maxFs * 0.85;
        const sup = t.y > baseline + subThresh && t.fontSize < maxFs * 0.85;
        let text = t.str;
        if (next) {
            const gap = next.x - (t.x + t.width);
            if (gap > t.fontSize * 0.18 && !/^\s/.test(next.str) && !/\s$/.test(text) && !/^[,.;:!?)]/.test(next.str)) {
                text += " ";
            }
        } else if (t.hasEOL && !/\s$/.test(text)) {
            text += " ";
        }
        runs.push({
            text,
            italic: t.italic && !sub && !sup,
            bold: t.bold,
            sub: sub || undefined,
            sup: sup || undefined,
        });
    }
    return mergeRuns(runs);
}

function lineText(runs: PdfInlineRun[]): string {
    return runs.map((r) => r.text).join("").trim();
}

function lineKind(runs: PdfInlineRun[], fontSize: number, bodySize: number): PdfBlock["kind"] {
    const text = lineText(runs);
    const letters = Math.max(1, text.replace(/\s/g, "").length);
    const italicShare = runs.reduce((n, r) => n + (r.italic ? r.text.length : 0), 0) / letters;
    const boldShare = runs.reduce((n, r) => n + (r.bold ? r.text.length : 0), 0) / letters;
    if (fontSize >= bodySize * 1.38 && text.length < 90) return "h1";
    if (fontSize >= bodySize * 1.12 && text.length < 90) return "h2";
    if (boldShare > 0.75 && text.length < 60 && !/[.!?]$/.test(text)) return "h2";
    if (italicShare > 0.7 && fontSize <= bodySize * 1.08 && text.length < 140 && text.length > 8) return "byline";
    return "p";
}

export function blocksFromPdfTextContent(content: PdfJsTextContent): PdfBlock[] {
    const styles = content.styles ?? {};
    const tokens: Token[] = [];
    for (const item of content.items) {
        if (!isTextItem(item) || !isHorizontal(item.transform)) continue;
        if (!item.str && !item.hasEOL) continue;
        const style = styles[item.fontName];
        tokens.push({
            str: item.str,
            x: item.transform[4],
            y: item.transform[5],
            width: item.width,
            fontSize: fontSizeOf(item.transform) || item.height || 12,
            italic: looksItalic(item.fontName, style?.fontFamily),
            bold: looksBold(item.fontName, style?.fontFamily),
            hasEOL: Boolean(item.hasEOL),
        });
    }
    if (tokens.length === 0) return [];

    tokens.sort((a, b) => b.y - a.y || a.x - b.x);

    const lines: Token[][] = [];
    let current: Token[] = [];
    let currentY = 0;
    let currentSize = 0;
    for (const token of tokens) {
        if (current.length === 0) {
            current = [token];
            currentY = token.y;
            currentSize = token.fontSize;
            continue;
        }
        const thresh = Math.max(currentSize, token.fontSize) * 0.5;
        if (Math.abs(token.y - currentY) <= thresh) {
            current.push(token);
            if (token.fontSize > currentSize) {
                currentY = token.y;
                currentSize = token.fontSize;
            }
        } else {
            lines.push(current.sort((a, b) => a.x - b.x));
            current = [token];
            currentY = token.y;
            currentSize = token.fontSize;
        }
    }
    if (current.length) lines.push(current.sort((a, b) => a.x - b.x));

    const lineMeta = lines.map((line) => {
        const runs = lineRuns(line);
        const fontSize = Math.max(...line.map((t) => t.fontSize));
        const y = median(line.filter((t) => t.fontSize >= fontSize * 0.85).map((t) => t.y));
        return { runs, fontSize, y };
    }).filter((line) => lineText(line.runs).length > 0);

    if (lineMeta.length === 0) return [];

    const bodySize = median(lineMeta.map((l) => l.fontSize));
    const gaps = lineMeta.slice(0, -1).map((line, i) => line.y - lineMeta[i + 1].y);
    const typicalGap = median(gaps.filter((g) => g > 0)) || bodySize * 1.2;

    const blocks: PdfBlock[] = [];
    let paragraph: PdfInlineRun[] | null = null;

    const flushParagraph = () => {
        if (paragraph && lineText(paragraph)) {
            blocks.push({ kind: "p", runs: mergeRuns(paragraph) });
        }
        paragraph = null;
    };

    for (let i = 0; i < lineMeta.length; i++) {
        const line = lineMeta[i];
        const kind = lineKind(line.runs, line.fontSize, bodySize);
        const gapBefore = i === 0 ? Infinity : lineMeta[i - 1].y - line.y;
        const newParagraph = gapBefore > typicalGap * 1.35;

        if (kind !== "p") {
            flushParagraph();
            blocks.push({ kind, runs: line.runs });
            continue;
        }
        if (!paragraph || newParagraph) {
            flushParagraph();
            paragraph = [...line.runs];
        } else {
            const last = paragraph[paragraph.length - 1];
            if (last && !/\s$/.test(last.text) && !/^\s/.test(line.runs[0]?.text ?? "")) {
                last.text += " ";
            }
            paragraph.push(...line.runs);
        }
    }
    flushParagraph();
    return blocks;
}

export function pdfBlocksCharCount(blocks: PdfBlock[]): number {
    return blocks.reduce((n, block) => n + block.runs.reduce((m, run) => m + run.text.length, 0), 0);
}
